import {
  EuiButton,
  EuiButtonEmpty,
  EuiForm,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
} from "@inscopix/ideas-eui";
import { ReactNode, useState } from "react";
import { useDataTableContext } from "../store/DataTableProvider";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { FieldTableName } from "./fields/FieldTableName";
import { FieldTableKey } from "./fields/FieldTableKey";
import { DataTableStoreState } from "../store/DataTableProvider.types";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { capitalize } from "lodash";

type Fields = {
  name: {
    value: string;
    isValid: boolean;
  };
  key: {
    value: string;
    isValid: boolean;
  };
};

export interface ModalEditTableBaseProps {
  table: DataTableStoreState["tables"][number];
  onClose: () => void;
  onSubmit: (fields: {
    name: string;
    key: string;
  }) => Promise<{ error?: unknown }>;
  children?: ReactNode;
}

export const ModalEditTableBase = ({
  table,
  onClose,
  onSubmit,
  children,
}: ModalEditTableBaseProps) => {
  const projectKey = useDataTableContext((s) => s.project.key);
  const tenantKey = useTenantContext((s) => s.currentTenant.key);
  const replaceTableRoute = useRouteMapContext(
    (s) => (tableKey: string) =>
      s.routeMap["PROJECT_TABLE"]
        .dynamicPath({ projectKey, tableKey, tenantKey })
        .navigateToReplace(),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState<Fields>({
    name: {
      value: table.name,
      isValid: true,
    },
    key: {
      value: table.key,
      isValid: true,
    },
  });

  const handleFieldChange = <T extends keyof Fields>(
    fieldName: T,
    field: Fields[T],
  ) => {
    setFields((prevFields) => ({
      ...prevFields,
      [fieldName]: field,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const isKeyChanged = fields.key.value !== table.key;

    const { error } = await onSubmit({
      name: fields.name.value,
      key: fields.key.value,
    });

    setIsLoading(false);

    if (error === undefined) {
      addUtilityToastSuccess(`${capitalize(table.kind)} table updated`);
      if (isKeyChanged) {
        replaceTableRoute(fields.key.value);
      }
    } else {
      addUtilityToastFailure(`Failed to update ${table.kind} table`);
    }

    onClose();
  };

  return (
    <EuiModal onClose={onClose}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          Edit {capitalize(table.kind)} Table
        </EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiForm>
          <FieldTableName
            autoFocus
            value={fields.name.value}
            onChange={(field) => handleFieldChange("name", field)}
          />
          <FieldTableKey
            tableName={fields.name.value}
            value={fields.key.value}
            onChange={(field) => handleFieldChange("key", field)}
          />
          {children}
        </EuiForm>
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
        <EuiButton
          isLoading={isLoading}
          fill
          onClick={() => void handleSubmit()}
          disabled={Object.values(fields).some((field) => !field.isValid)}
        >
          Update
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};
